<template>
  <div class="admin-locations">
    <div class="header">
      <h2>{{ $t('pages.AdminLocations.admin_house_locations_11562f6') }}</h2>
      <p class="subtitle">
        {{ $t('pages.AdminLocations.drag_any_pin_to_correct_its_locati_46eadc8') }}
      </p>
    </div>

    <div class="layout">
      <aside class="sidebar">
        <div class="search-row">
          <input
            v-model="search"
            type="text"
            :placeholder="$t('pages.AdminLocations.filter_by_name_district_area_manag_fd68d57')"
            class="search-input"
          />
        </div>
        <div class="counts">
          <span>Total: {{ rooms.length }}</span>
          <span>Unpinned: {{ unpinnedCount }}</span>
        </div>
        <div class="list">
          <div
            v-for="r in filteredRooms"
            :key="r.houseLetId"
            class="list-item"
            :class="{ active: selectedId === r.houseLetId, unpinned: r.lat == null || r.lon == null }"
            @click="focusRoom(r)"
          >
            <div class="primary">{{ r.houseName }} <span class="muted">·</span> {{ r.houseLetName || '—' }}</div>
            <div class="meta">
              <span>{{ [r.area, r.district].filter(Boolean).join(' · ') || '—' }}</span>
              <span v-if="r.managedByName" class="badge">{{ r.managedByName }}</span>
              <span v-if="r.lat == null || r.lon == null" class="badge warn">{{ $t('pages.AdminLocations.no_pin_8d7ac9d') }}</span>
            </div>
          </div>
          <div v-if="filteredRooms.length === 0" class="empty">{{ $t('pages.AdminLocations.no_matches_0be6a25') }}</div>
        </div>
      </aside>

      <div ref="adminMap" class="map"></div>
    </div>

    <div v-if="saving" class="saving-toast">{{ $t('pages.AdminLocations.saving_56a2285') }}</div>
    <div v-if="lastSaveMsg" class="saving-toast success">{{ lastSaveMsg }}</div>
  </div>
</template>

<script>
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Distinct icon for "no pin yet" so admin sees what still needs work at a glance.
const unpinnedIcon = new L.Icon({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  className: 'unpinned-marker',
});

export default {
  name: 'AdminLocations',
  data() {
    return {
      rooms: [],
      markers: {},
      selectedId: null,
      search: '',
      saving: false,
      lastSaveMsg: '',
      lastSaveTimer: null,
    };
  },
  computed: {
    filteredRooms() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.rooms;
      return this.rooms.filter((r) => {
        const hay = [
          r.houseName, r.houseLetName, r.region, r.district, r.area,
          r.street, r.address, r.managedByName,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    },
    unpinnedCount() {
      return this.rooms.filter(r => r.lat == null || r.lon == null).length;
    },
  },
  mounted() {
    this._initMap();
    this._fetchRooms();
  },
  beforeDestroy() {
    if (this._map) { this._map.remove(); this._map = null; }
    if (this.lastSaveTimer) clearTimeout(this.lastSaveTimer);
  },
  methods: {
    _initMap() {
      this._map = L.map(this.$refs.adminMap, { zoomControl: true }).setView([22.302711, 114.177216], 12);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(this._map);
    },
    _fetchRooms() {
      axios.get('/api/house/admin/locations')
        .then((resp) => {
          this.rooms = (resp.data && resp.data.data) || [];
          this._renderMarkers();
        })
        .catch((err) => {
          console.error('Failed to load admin locations:', err);
        });
    },
    _renderMarkers() {
      // Drop existing markers.
      Object.values(this.markers).forEach(m => this._map.removeLayer(m));
      this.markers = {};

      const points = [];
      this.rooms.forEach((r) => {
        if (r.lat == null || r.lon == null) return;
        const marker = L.marker([r.lat, r.lon], { draggable: true });
        marker.bindPopup(this._popupHtml(r));
        marker.on('dragend', (e) => {
          const ll = e.target.getLatLng();
          this._saveCoords(r, ll.lat, ll.lng);
        });
        marker.addTo(this._map);
        this.markers[r.houseLetId] = marker;
        points.push([r.lat, r.lon]);
      });
      if (points.length > 0) {
        try { this._map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 15 }); } catch (e) {}
      }
    },
    _popupHtml(r) {
      const title = `${r.houseName || ''} · ${r.houseLetName || ''}`.trim();
      const place = [r.area, r.district].filter(Boolean).join(' · ');
      return `<strong>${title}</strong><br>${place}`;
    },
    focusRoom(r) {
      this.selectedId = r.houseLetId;
      if (r.lat != null && r.lon != null) {
        const m = this.markers[r.houseLetId];
        if (m) {
          this._map.setView([r.lat, r.lon], 17);
          m.openPopup();
        }
      } else {
        // No pin yet: drop one at the current map center so admin can drag it to the right spot.
        const c = this._map.getCenter();
        this._saveCoords(r, c.lat, c.lng, /* afterSaveOpen */ true);
      }
    },
    _saveCoords(r, lat, lon, afterSaveOpen = false) {
      this.saving = true;
      axios.post('/api/location/updateCoords', {
        houseId: r.houseId,
        houseLetId: r.houseLetId,
        lat,
        lon,
      })
        .then(() => {
          this._setSaveMsg(`Saved ${r.houseName || ''} · ${r.houseLetName || ''}`);
          r.lat = lat;
          r.lon = lon;
          let m = this.markers[r.houseLetId];
          if (!m) {
            m = L.marker([lat, lon], { draggable: true });
            m.bindPopup(this._popupHtml(r));
            m.on('dragend', (e) => {
              const ll = e.target.getLatLng();
              this._saveCoords(r, ll.lat, ll.lng);
            });
            m.addTo(this._map);
            this.markers[r.houseLetId] = m;
          } else {
            m.setLatLng([lat, lon]);
          }
          if (afterSaveOpen) {
            this._map.setView([lat, lon], 17);
            m.openPopup();
          }
        })
        .catch((err) => {
          console.error('Save failed:', err);
          this._setSaveMsg('Save failed — see console.');
        })
        .finally(() => { this.saving = false; });
    },
    _setSaveMsg(msg) {
      this.lastSaveMsg = msg;
      if (this.lastSaveTimer) clearTimeout(this.lastSaveTimer);
      this.lastSaveTimer = setTimeout(() => { this.lastSaveMsg = ''; }, 2200);
    },
  },
};
</script>

<style scoped>
.admin-locations { padding: 16px 24px; }
.header h2 { margin: 0 0 4px; }
.subtitle { color: #666; font-size: 13px; margin: 0 0 12px; }
.layout { display: grid; grid-template-columns: 360px 1fr; gap: 16px; height: calc(100vh - 160px); }
.sidebar { display: flex; flex-direction: column; min-height: 0; border: 1px solid #e3eaf2; border-radius: 12px; overflow: hidden; background: #fff; }
.search-row { padding: 12px; border-bottom: 1px solid #eef2f7; }
.search-input { width: 100%; padding: 8px 10px; border: 1px solid #d8e0ea; border-radius: 8px; font-size: 14px; }
.counts { padding: 8px 12px; font-size: 12px; color: #777; display: flex; gap: 12px; border-bottom: 1px solid #eef2f7; background: #f8fafc; }
.list { flex: 1; overflow: auto; }
.list-item { padding: 10px 12px; border-bottom: 1px solid #eef2f7; cursor: pointer; }
.list-item:hover { background: #f5f8fc; }
.list-item.active { background: #e9f4ff; }
.list-item.unpinned .primary::after { content: ' ·'; color: #d97706; }
.primary { font-size: 14px; color: #111; }
.muted { color: #aaa; }
.meta { font-size: 12px; color: #666; margin-top: 4px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.badge { background: #eef2f7; color: #335; padding: 1px 6px; border-radius: 6px; font-size: 11px; }
.badge.warn { background: #fff1e2; color: #b45309; }
.map { border: 1px solid #e3eaf2; border-radius: 12px; }
.empty { padding: 24px; text-align: center; color: #888; font-size: 13px; }
.saving-toast { position: fixed; bottom: 24px; right: 24px; background: #111; color: #fff; padding: 10px 14px; border-radius: 10px; font-size: 13px; }
.saving-toast.success { background: #047857; }
.unpinned-marker { filter: hue-rotate(60deg) saturate(2); }
</style>
